import { 
  LayoutDashboard, 
  Heart,
  UserCircle,
  Calendar, 
  Receipt, 
  Watch, 
  Video, 
  Shield,
  Settings,
  HelpCircle,
  LogOut,
  Activity,
  Bell,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: string;
}

const mainNavItems: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: ROUTES.MEMBER },
  { icon: UserCircle, label: "My Profile", path: ROUTES.MEMBER_PROFILE },
  { icon: Heart, label: "My Health", path: ROUTES.MEMBER_HEALTH },
  { icon: Sparkles, label: "Health FAQ", path: ROUTES.MEMBER_HEALTH_FAQ },
  { icon: Calendar, label: "My Appointments", path: ROUTES.MEMBER_APPOINTMENTS },
  { icon: Bell, label: "Notifications", path: ROUTES.MEMBER_NOTIFICATIONS },
  { icon: Video, label: "Schedule Visit", path: ROUTES.MEMBER_SCHEDULE },
  { icon: Watch, label: "My Devices", path: ROUTES.MEMBER_DEVICES },
  { icon: Receipt, label: "Billing & Payments", path: ROUTES.MEMBER_BILLING },
  { icon: Shield, label: "Insurance", path: ROUTES.MEMBER_INSURANCE },
];

const bottomNavItems: NavItem[] = [
  { icon: Settings, label: "Settings", path: ROUTES.MEMBER_SETTINGS },
  { icon: HelpCircle, label: "Help & Support", path: ROUTES.MEMBER_HELP },
];

export function MemberSidebar() {
  const { signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r border-sidebar-border bg-sidebar">
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 border-b border-sidebar-border px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg gradient-primary">
            <Activity className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-lg font-bold text-sidebar-foreground">Vivien Health</h1>
            <p className="text-xs text-muted-foreground">Member Portal</p>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            My Health
          </p>
          {mainNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-sidebar-border px-3 py-4">
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button 
            onClick={signOut}
            className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <LogOut className="h-5 w-5" />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
